import React from 'react';
import { TONE_TEXT } from '../utils/statusColors';

/**
 * Uma linha de item: ▸, código, nome, selos, quantidade, unitário e total, com
 * a descrição comercial recolhida atrás do ▸.
 *
 * Um componente só para a FICHA DA ASSINATURA (olho) e para a FICHA DA FATURA,
 * para as duas contarem a mesma história com a mesma régua. A descrição serve
 * à proposta, onde o cliente decide; na ficha interna ela empurrava para baixo
 * o que se foi ver.
 */
export type TomDoTotal = 'verde' | 'ps' | 'azul' | 'negativo' | 'apagado';

// Tom do total por significado (§12.4), do mapa de statusColors.
const COR_DO_TOTAL: Record<TomDoTotal, string> = {
  verde: TONE_TEXT.open, // recorrente
  // P&S: tom próprio `ps` (teal), decisão de 04/10/2026. Não é laranja (acento da
  // ação primária, §8.1) nem warning (P&S é categoria, não atenção).
  ps: TONE_TEXT.ps,
  azul: TONE_TEXT.progress, // consumo
  negativo: TONE_TEXT.danger, // desconto: sempre vermelho
  apagado: TONE_TEXT.neutral, // fora de vigência (a linha também esmaece)
};

interface Props {
  codigo?: string | null;
  nome: string;
  /** Texto miúdo depois do nome (ex.: "sobre Licença de Ramal"). */
  complemento?: string | null;
  titulo?: string;
  descricao?: string | null;
  aberta: boolean;
  onAlternar: () => void;
  /** Selos entre o nome e os números (vigência, parcelas, rateado/próprio). */
  selos?: React.ReactNode;
  quantidade: React.ReactNode;
  unitario: React.ReactNode;
  total: string;
  tom: TomDoTotal;
  /** Linha fora do que vale hoje: esmaecida. */
  esmaecida?: boolean;
}

const LinhaDeItem: React.FC<Props> = ({ codigo, nome, complemento, titulo, descricao, aberta, onAlternar, selos, quantidade, unitario, total, tom, esmaecida }) => (
  <div className='border-b border-slate-800 last:border-b-0'>
    <div className={`flex items-center gap-3 py-2 ${esmaecida ? 'opacity-60' : ''}`}>
      <button
        type='button'
        onClick={() => descricao && onAlternar()}
        className={`w-4 shrink-0 text-xs ${descricao ? 'text-slate-500 hover:text-white' : 'text-transparent cursor-default'}`}
        title={descricao ? (aberta ? 'Esconder descrição' : 'Ver descrição') : undefined}
        aria-expanded={descricao ? aberta : undefined}
      >
        {aberta ? '▾' : '▸'}
      </button>
      {codigo && <span className='shrink-0 font-mono text-[10px] text-slate-400 bg-slate-800 px-1.5 py-0.5 rounded'>{codigo}</span>}
      <div className='flex-1 min-w-0 text-sm text-slate-200 truncate' title={titulo || nome}>
        {nome}
        {complemento && <span className='text-xs text-slate-500'> · {complemento}</span>}
      </div>
      {selos}
      <span className='w-12 shrink-0 text-right font-mono text-xs text-slate-400'>{quantidade}</span>
      <span className='w-24 shrink-0 text-right font-mono text-xs text-slate-400 whitespace-nowrap'>{unitario}</span>
      <span className={`w-24 shrink-0 text-right font-mono text-sm font-medium whitespace-nowrap ${COR_DO_TOTAL[tom]}`}>{total}</span>
    </div>
    {aberta && descricao && (
      <div className='pl-7 pb-2.5 pr-2 text-xs text-slate-400 leading-relaxed whitespace-pre-line'>{descricao}</div>
    )}
  </div>
);

export default LinhaDeItem;
